import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Truck, LogIn as CheckIn, LogOut as CheckOut, Plus, AlertTriangle, Trash2, Edit } from 'lucide-react';
import { toast } from 'sonner';
import { Textarea } from '@/components/ui/textarea';
import DamageInspection from '@/components/DamageInspection';
import BulkActionsBar from '@/components/BulkActionsBar';

const Vehicles = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [checkOutOpen, setCheckOutOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [kmReading, setKmReading] = useState('');
  const [checkInDamages, setCheckInDamages] = useState([]);
  const [noDamages, setNoDamages] = useState(false);
  const [damages, setDamages] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedVehicles, setSelectedVehicles] = useState([]);

  useEffect(() => {
    loadVehicles();
  }, []);

  const loadVehicles = async () => {
    try {
      const res = await axios.get('/vehicles');
      // Handle both old format (array) and new format (object with vehicles array)
      const vehicleData = res.data.vehicles || res.data;
      setVehicles(vehicleData);
    } catch (error) {
      console.error('Error loading vehicles:', error);
      toast.error('Fehler beim Laden der Fahrzeuge');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectVehicle = (vehicleId) => {
    setSelectedVehicles(prev => 
      prev.includes(vehicleId) 
        ? prev.filter(id => id !== vehicleId)
        : [...prev, vehicleId]
    );
  };

  const handleSelectAll = () => {
    if (selectedVehicles.length === vehicles.length) {
      setSelectedVehicles([]);
    } else {
      setSelectedVehicles(vehicles.map(v => v.vehicle_id));
    }
  };

  const handleBulkUpdateStatus = async (status) => {
    try {
      await axios.post('/bulk/vehicles/update-status', {
        vehicle_ids: selectedVehicles,
        status
      });
      toast.success(`${selectedVehicles.length} Fahrzeuge aktualisiert`);
      setSelectedVehicles([]);
      loadVehicles();
    } catch (error) {
      console.error('Error updating vehicles:', error);
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleBulkDelete = async () => {
    try {
      await axios.delete('/bulk/vehicles', {
        data: { vehicle_ids: selectedVehicles }
      });
      toast.success(`${selectedVehicles.length} Fahrzeuge gelöscht`);
      setSelectedVehicles([]);
      loadVehicles();
    } catch (error) {
      console.error('Error deleting vehicles:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const bulkActions = [
    {
      key: 'status',
      label: 'Status ändern',
      type: 'select',
      options: [
        { value: 'available', label: 'Verfügbar' },
        { value: 'in_use', label: 'Im Einsatz' },
        { value: 'maintenance', label: 'Wartung' }
      ],
      handler: handleBulkUpdateStatus
    },
    {
      key: 'delete',
      label: 'Löschen',
      icon: Trash2,
      variant: 'destructive',
      requiresConfirm: true,
      handler: handleBulkDelete
    }
  ];

  const handleCheckIn = async () => {
    if (!kmReading) {
      toast.error('Bitte Kilometerstand eingeben');
      return;
    }

    if (!noDamages && checkInDamages.length === 0) {
      toast.error('Bitte führen Sie die Schadensinspektion durch');
      return;
    }

    try {
      await axios.post('/check-in/inspection', {
        vehicle_id: selectedVehicle.vehicle_id,
        km_reading: parseInt(kmReading),
        damages_found: checkInDamages,
        no_damages: noDamages,
        notes
      });
      
      toast.success(checkInDamages.length > 0 
        ? `Fahrzeug übernommen - ${checkInDamages.length} Schaden/Schäden dokumentiert`
        : 'Fahrzeug erfolgreich übernommen - keine Schäden'
      );
      setCheckInOpen(false);
      setKmReading('');
      setCheckInDamages([]);
      setNoDamages(false);
      setNotes('');
      loadVehicles();
    } catch (error) {
      console.error('Check-in error:', error);
      toast.error('Fehler beim Check-in');
    }
  };

  const handleCheckOut = async () => {
    if (!kmReading) {
      toast.error('Bitte Kilometerstand eingeben');
      return;
    }

    try {
      await axios.post('/vehicles/checkout', {
        vehicle_id: selectedVehicle.vehicle_id,
        km_reading: parseInt(kmReading),
        issues: damages ? damages.split('\n') : []
      });
      
      toast.success('Fahrzeug erfolgreich zurückgegeben');
      setCheckOutOpen(false);
      setKmReading('');
      setDamages('');
      loadVehicles();
    } catch (error) {
      console.error('Check-out error:', error);
      toast.error('Fehler beim Check-out');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="vehicles-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight uppercase text-white mb-2">
            Fahrzeuge
          </h1>
          <p className="text-zinc-400">Fuhrparkverwaltung und Fahrzeugstatus</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {vehicles.map(vehicle => {
          const tuvDate = new Date(vehicle.tuv_expiry);
          const now = new Date();
          const daysUntilTuv = Math.ceil((tuvDate - now) / (1000 * 60 * 60 * 24));
          const tuvWarning = daysUntilTuv < 30;
          
          return (
            <Card 
              key={vehicle.vehicle_id} 
              className="bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition-all"
              data-testid={`vehicle-card-${vehicle.vehicle_id}`}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-full bg-zinc-800 flex items-center justify-center">
                      <Truck className="h-6 w-6 text-zinc-400" />
                    </div>
                    <div>
                      <CardTitle className="text-xl font-bold text-white">{vehicle.license_plate}</CardTitle>
                      <p className="text-sm text-zinc-400">{vehicle.model}</p>
                    </div>
                  </div>
                  <Badge 
                    variant={vehicle.status === 'in_use' ? 'default' : vehicle.status === 'maintenance' ? 'destructive' : 'secondary'}
                  >
                    {vehicle.status === 'in_use' ? 'Im Einsatz' : vehicle.status === 'maintenance' ? 'Wartung' : 'Verfügbar'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-zinc-500">Kilometerstand</div>
                    <div className="font-mono text-white font-medium">{vehicle.current_km.toLocaleString()} km</div>
                  </div>
                  <div>
                    <div className="text-zinc-500">TÜV abläuft</div>
                    <div className={`font-medium ${
tuvWarning ? 'text-red-400 flex items-center gap-1' : 'text-white'
                    }`}>
                      {tuvWarning && <AlertTriangle className="h-4 w-4" />}
                      {daysUntilTuv} Tage
                    </div>
                  </div>
                </div>
                
                {vehicle.status === 'available' ? (
                  <Dialog open={checkInOpen && selectedVehicle?.vehicle_id === vehicle.vehicle_id} onOpenChange={setCheckInOpen}>
                    <DialogTrigger asChild>
                      <Button 
                        className="w-full bg-primary hover:bg-primary/90" 
                        onClick={() => setSelectedVehicle(vehicle)}
                        data-testid={`checkin-btn-${vehicle.vehicle_id}`}
                      >
                        <CheckIn className="mr-2 h-4 w-4" />
                        Fahrzeug übernehmen
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="text-white">Fahrzeug übernehmen</DialogTitle>
                        <DialogDescription>
                          {vehicle.license_plate} - {vehicle.model}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="km">Kilometerstand *</Label>
                          <Input 
                            id="km"
                            type="number" 
                            value={kmReading} 
                            onChange={(e) => setKmReading(e.target.value)}
                            placeholder="Aktueller Stand"
                            className="bg-zinc-950 border-zinc-800"
                          />
                        </div>

                        {/* Damage Inspection Component */}
                        <DamageInspection 
                          onDamagesChange={setCheckInDamages}
                          onNoDamagesChange={setNoDamages}
                        />

                        <div>
                          <Label htmlFor="notes">Zusätzliche Notizen (optional)</Label>
                          <Textarea 
                            id="notes"
                            value={notes} 
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Zusätzliche Anmerkungen zum Check-in..."
                            className="bg-zinc-950 border-zinc-800"
                            rows={2}
                          />
                        </div>
                        <Button onClick={handleCheckIn} className="w-full bg-green-600 hover:bg-green-700" data-testid="confirm-checkin-btn">
                          Check-in bestätigen
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                ) : vehicle.status === 'in_use' ? (
                  <Dialog open={checkOutOpen && selectedVehicle?.vehicle_id === vehicle.vehicle_id} onOpenChange={setCheckOutOpen}>
                    <DialogTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="w-full border-zinc-700" 
                        onClick={() => setSelectedVehicle(vehicle)}
                        data-testid={`checkout-btn-${vehicle.vehicle_id}`}
                      >
                        <CheckOut className="mr-2 h-4 w-4" />
                        Fahrzeug zurückgeben
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-zinc-900 border-zinc-800">
                      <DialogHeader>
                        <DialogTitle className="text-white">Fahrzeug zurückgeben</DialogTitle>
                        <DialogDescription>
                          {vehicle.license_plate} - {vehicle.model}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="km-out">Kilometerstand *</Label>
                          <Input 
                            id="km-out"
                            type="number" 
                            value={kmReading} 
                            onChange={(e) => setKmReading(e.target.value)}
                            placeholder="Aktueller Stand"
                            className="bg-zinc-950 border-zinc-800"
                          />
                        </div>
                        <div>
                          <Label htmlFor="issues">Probleme (optional, eine pro Zeile)</Label>
                          <Textarea 
                            id="issues"
                            value={damages} 
                            onChange={(e) => setDamages(e.target.value)}
                            placeholder="Probleme während der Fahrt..."
                            className="bg-zinc-950 border-zinc-800"
                            rows={3}
                          />
                        </div>
                        <Button onClick={handleCheckOut} className="w-full" data-testid="confirm-checkout-btn">
                          Bestätigen
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                ) : null}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default Vehicles;
